import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CapaType, CapaCategory, CapaPriority, CapaSeverity, CapaTriggerSource } from '../types';
import { capaService } from '../capaService';

interface CreateCapaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateCapaModal: React.FC<CreateCapaModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [title, setTitle] = useState('');
  const [problemStatement, setProblemStatement] = useState('');
  const [description, setDescription] = useState('');
  const [capaType, setCapaType] = useState<CapaType>('CORRECTIVE_AND_PREVENTIVE');
  const [category, setCategory] = useState<CapaCategory>('OPERATIONS');
  const [priority, setPriority] = useState<CapaPriority>('HIGH');
  const [severity, setSeverity] = useState<CapaSeverity>('MAJOR');
  const [triggerSource, setTriggerSource] = useState<CapaTriggerSource>('FINDING');
  const [triggerReference, setTriggerReference] = useState('');
  const [department, setDepartment] = useState('Flight Operations');
  const [location, setLocation] = useState('LTFM / IST');
  const [ownerId, setOwnerId] = useState('MGR-OPS-01');
  const [ownerName, setOwnerName] = useState('Capt. Flight Ops Manager');
  const [coordinatorName, setCoordinatorName] = useState('Quality & Safety Coordinator');
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)
  );
  const [authorityDueDate, setAuthorityDueDate] = useState('');
  const [isSystemic, setIsSystemic] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [requiresRootCause, setRequiresRootCause] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [requiresVerification, setRequiresVerification] = useState(true);
  const [requiresEffectiveness, setRequiresEffectiveness] = useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !problemStatement.trim()) {
      setError('Please provide both Title and Problem Statement.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await capaService.createCapa({
        title,
        problemStatement,
        description,
        capaType,
        category,
        priority,
        severity,
        triggerSource,
        triggerReference: triggerReference || undefined,
        department,
        location,
        ownerId,
        ownerName,
        coordinatorId: 'COORD-01',
        coordinatorName,
        dueDate,
        authorityDueDate: authorityDueDate || undefined,
        requiresRootCause,
        requiresApproval,
        requiresVerification,
        requiresEffectiveness,
        isSystemic,
        estimatedCost: Number(estimatedCost) || 0,
        createdBy: 'CURRENT_USER',
      });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to initiate CAPA.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Initiate Corrective & Preventive Action (CAPA)</h3>
            <p>
              Structured systemic containment, 5-Why root cause analysis, and closed-loop verification.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div className="form-field">
              <label>CAPA Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Flight Planning Alternate Fuel Procedural Deviation Prevention"
              />
            </div>

            <div className="form-field">
              <label>Problem Statement (Clear & Measurable Non-Conformance) *</label>
              <textarea
                required
                rows={2}
                value={problemStatement}
                onChange={(e) => setProblemStatement(e.target.value)}
                placeholder="Specific factual summary of what failed, standard violated, and operational consequence..."
              />
            </div>

            <div className="form-field">
              <label>Detailed Scope & Background Description</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Contextual background, fleet impact, or systemic indicators..."
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>CAPA Type</label>
                <select
                  value={capaType}
                  onChange={(e) => setCapaType(e.target.value as CapaType)}
                >
                  <option value="CORRECTIVE">Corrective Action</option>
                  <option value="PREVENTIVE">Preventive Action</option>
                  <option value="CORRECTIVE_AND_PREVENTIVE">Corrective & Preventive (CAPA)</option>
                  <option value="SYSTEMIC">Systemic Improvement</option>
                  <option value="REGULATORY">Regulatory Compliance Mandate</option>
                  <option value="SAFETY">Safety Action</option>
                  <option value="QUALITY">Quality Action</option>
                </select>
              </div>

              <div className="form-field">
                <label>Domain Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as CapaCategory)}
                >
                  <option value="OPERATIONS">Flight Operations</option>
                  <option value="MAINTENANCE">Maintenance & Engineering</option>
                  <option value="AIRWORTHINESS">Continuing Airworthiness (CAMO)</option>
                  <option value="GROUND_OPS">Ground Handling & Ramp</option>
                  <option value="SECURITY">Aviation Security</option>
                  <option value="TRAINING">Crew & Staff Training</option>
                  <option value="DOCUMENTATION">Documentation & Manuals</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Priority Level</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as CapaPriority)}
                >
                  <option value="CRITICAL">Critical (Immediate Escalation)</option>
                  <option value="HIGH">High Priority</option>
                  <option value="MEDIUM">Medium Priority</option>
                  <option value="LOW">Low Priority</option>
                </select>
              </div>

              <div className="form-field">
                <label>Trigger Source</label>
                <select
                  value={triggerSource}
                  onChange={(e) => setTriggerSource(e.target.value as CapaTriggerSource)}
                >
                  <option value="FINDING">Audit / Inspection Finding</option>
                  <option value="AUDIT">Quality Audit</option>
                  <option value="INSPECTION">Ramp / Facility Inspection</option>
                  <option value="OCCURRENCE">Safety Occurrence Report</option>
                  <option value="INVESTIGATION">Investigation Recommendation</option>
                  <option value="RISK">Risk Assessment Mitigation</option>
                  <option value="AUTHORITY">Civil Aviation Authority (EASA/FAA)</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Trigger Source Reference</label>
                <input
                  type="text"
                  value={triggerReference}
                  onChange={(e) => setTriggerReference(e.target.value)}
                  placeholder="e.g. FND-2026-000001 or AUD-OPS-2026"
                />
              </div>

              <div className="form-field">
                <label>Responsible Department *</label>
                <input
                  type="text"
                  required
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="e.g. Flight Operations"
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Action Owner (Assignee) *</label>
                <input
                  type="text"
                  required
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="e.g. Capt. Flight Operations Manager"
                />
              </div>

              <div className="form-field">
                <label>Target Due Date *</label>
                <input
                  type="date"
                  required
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Authority Due Date (If Applicable)</label>
                <input
                  type="date"
                  value={authorityDueDate}
                  onChange={(e) => setAuthorityDueDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Estimated Cost ($ USD)</label>
                <input
                  type="number"
                  value={estimatedCost}
                  onChange={(e) => setEstimatedCost(Number(e.target.value))}
                />
              </div>
            </div>

            {/* Governance Checkboxes */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px 16px' }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                Governance & Verification Prerequisites
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '13px', color: '#475569' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isSystemic}
                    onChange={(e) => setIsSystemic(e.target.checked)}
                  />
                  <span>Systemic Problem</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresRootCause}
                    onChange={(e) => setRequiresRootCause(e.target.checked)}
                  />
                  <span>Root Cause Analysis Mandatory</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresVerification}
                    onChange={(e) => setRequiresVerification(e.target.checked)}
                  />
                  <span>Independent Verification Mandatory</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requiresEffectiveness}
                    onChange={(e) => setRequiresEffectiveness(e.target.checked)}
                  />
                  <span>Effectiveness Review Mandatory</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {saving ? 'Initiating...' : 'Initiate CAPA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCapaModal;
